import type { ServiceLinkFilterRule } from '@/types/policy/PolicyTypes'
import { isTrue } from '@/utils/CommonUtils'

/**
 * Gibt die tenant-spezifischen Service-Link-Filterregeln für DA-Direkt zurück.
 *
 * Die Funktion liest zur Laufzeit die Feature-Flags aus `globalThis.ENV_CONFIG.FEATURE_FLAGS`
 * und nimmt feature-flag-abhängige Links nur dann in das Regelarray auf, wenn das jeweilige
 * Flag aktiv ist. Alle anderen Bedingungen (DDS-Line, WKZ, Status, RedCoop) werden durch die
 * Felder der {@link ServiceLinkFilterRule} abgebildet und von `resolveServiceLinksFromRules`
 * ausgewertet.
 *
 * **Regelreihenfolge:** Bei gleichem `linkname` gewinnt die erste passende Regel (first-match-wins).
 * Spezifischere Regeln (z. B. für einen bestimmten Fahrzeugtyp) sollten daher VOR allgemeineren
 * Regeln (z. B. BUNDLE) platziert werden.
 *
 * **Neue Regel anlegen:**
 * ```ts
 * {
 *   linkname: 'meinLink',          // i18n-Schlüssel + ServiceLink.name
 *   hrefConfigKey: 'MEIN_LINK',    // Schlüssel in ENV_CONFIG.SERVICE_LINKS
 *   allowedDDSLines: ['Motor'],    // oder ['*'] für alle
 *   allowedRedCoop: true,          // true = auch für RedCoop-Partner sichtbar
 *   allowedStatus: ['ACTIVE'],     // oder ['*'] für alle Status
 *   allowedWKZ: ['112'],           // WKZ-Präfixe oder ['*'] für alle
 *   canceledSinceInDays: 0         // 0 = keine Storno-Tages-Einschränkung
 * }
 * ```
 */
export function getServiceLinkRules(): ServiceLinkFilterRule[] {
  const featureFlags = globalThis.ENV_CONFIG?.FEATURE_FLAGS
  const rules: ServiceLinkFilterRule[] = []

  // ──────────────────────────────────────────────────────────────────────────────────
  // MOTOR – Fahrzeugwechsel (nur AUTO, kein RedCoop, nur wenn Feature-Flag aktiv)
  // ──────────────────────────────────────────────────────────────────────────────────
  if (isTrue(featureFlags?.MOTOR?.ENABLE_MOTOR_CHANGE)) {
    rules.push({
      linkname: 'vehicleChange',
      hrefConfigKey: 'VEHICLE_CHANGE',
      allowedDDSLines: ['Motor'],
      allowedRedCoop: false,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['112'],
      canceledSinceInDays: 0
    })
  }

  // ──────────────────────────────────────────────────────────────────────────────────
  // MOTOR – Motor-Cockpit Links (nur AUTO, kein RedCoop)
  // ──────────────────────────────────────────────────────────────────────────────────
  rules.push(
    {
      linkname: 'coverageChange',
      hrefConfigKey: 'COVERAGE_CHANGE',
      allowedDDSLines: ['Motor'],
      allowedRedCoop: false,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['112'],
      canceledSinceInDays: 0
    },
    {
      linkname: 'maxMileageChange',
      hrefConfigKey: 'MAX_MILEAGE_CHANGE',
      allowedDDSLines: ['Motor'],
      allowedRedCoop: false,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['112'],
      canceledSinceInDays: 0
    },
    {
      linkname: 'circleOfDriversChange',
      hrefConfigKey: 'CIRCLE_OF_DRIVERS_CHANGE',
      allowedDDSLines: ['Motor'],
      allowedRedCoop: false,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['112'],
      canceledSinceInDays: 0
    }
  )

  // ──────────────────────────────────────────────────────────────────────────────────
  // MOTOR – Internationale Versicherungskarte / Green Card
  // Alle Motor-Vertragstypen, KEINE WKZ-Prüfung (auch eMobil mit leerem WKZ-String
  // würde nicht matchen, da allowedWKZ: ['*'] nur zieht wenn wkz gesetzt ist).
  // Sichtbar für RedCoop und nicht-RedCoop.
  // ──────────────────────────────────────────────────────────────────────────────────
  rules.push({
    linkname: 'requestGreenCard',
    hrefConfigKey: 'REQUEST_GREEN_CARD',
    allowedDDSLines: ['Motor'],
    allowedRedCoop: true,
    allowedStatus: ['ACTIVE'],
    allowedWKZ: ['*'],
    canceledSinceInDays: 0
  })

  // ──────────────────────────────────────────────────────────────────────────────────
  // MOTOR – Digitale Schadenkarte (WKZ 112)
  // Nur Status ACTIVE, Feature-Flag muss aktiv sein.
  // ──────────────────────────────────────────────────────────────────────────────────
  if (isTrue(featureFlags?.ENABLE_DIGITAL_CARD)) {
    rules.push({
      linkname: 'requestDigitalCard',
      hrefConfigKey: 'REQUEST_DIGITAL_CARD',
      allowedDDSLines: ['Motor'],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['112'],
      canceledSinceInDays: 0
    })

    // Rechtsschutz (SHUR) erhält die Digitale Schadenkarte nur bei aktiviertem
    // spartenspezifischem Feature-Flag.
    if (isTrue(featureFlags?.ENABLE_DIGITAL_CARD_LEGAL_PROTECTION)) {
      rules.push({
        linkname: 'requestDigitalCard',
        hrefConfigKey: 'REQUEST_DIGITAL_CARD',
        allowedDDSLines: ['LegalProtection'],
        allowedRedCoop: true,
        allowedStatus: ['ACTIVE'],
        allowedWKZ: ['*'],
        canceledSinceInDays: 0
      })
    }

    // Krad-Produkte (Motorrad, Roller, Trike): WKZ 003, 012, 016, 018, 026, 028, 030
    // Nur wenn Feature-Flag ENABLE_DIGITAL_CARD_KRAD aktiv ist.
    if (isTrue(featureFlags?.ENABLE_DIGITAL_CARD_KRAD)) {
      rules.push({
        linkname: 'requestDigitalCard',
        hrefConfigKey: 'REQUEST_DIGITAL_CARD',
        allowedDDSLines: ['Motor'],
        allowedRedCoop: true,
        allowedStatus: ['ACTIVE'],
        allowedWKZ: ['003', '012', '016', '018', '026', '028', '030'],
        canceledSinceInDays: 0
      })
    }
  }

  // ──────────────────────────────────────────────────────────────────────────────────
  // MOTOR – Schaden melden (pro Fahrzeugtyp, beide RedCoop-Status)
  // Reihenfolge zählt: spezifischer Fahrzeugtyp vor allgemeinerer Fallback-Regel.
  // ──────────────────────────────────────────────────────────────────────────────────
  rules.push(
    {
      linkname: 'reportDamage',
      hrefConfigKey: 'REPORT_DAMAGE_AUTO',
      allowedDDSLines: ['Motor'],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['112'],
      canceledSinceInDays: 0
    },
    {
      linkname: 'reportDamage',
      hrefConfigKey: 'REPORT_DAMAGE_MOTORRAD',
      allowedDDSLines: ['Motor'],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['003', '012', '016', '018', '026', '028', '766', '768', '776', '778'],
      canceledSinceInDays: 0
    }
  )

  // ──────────────────────────────────────────────────────────────────────────────────
  // MOTOR – Vertragskündigung (alle Fahrzeugtypen, nur nicht-RedCoop)
  // ──────────────────────────────────────────────────────────────────────────────────
  rules.push({
    linkname: 'contractTermination',
    hrefConfigKey: 'CONTRACT_TERMINATION',
    allowedDDSLines: ['Motor'],
    allowedRedCoop: false,
    allowedStatus: ['ACTIVE'],
    allowedWKZ: ['*'],
    canceledSinceInDays: 0
  })
  rules.push({
    linkname: 'contractTermination',
    hrefConfigKey: 'CONTRACT_TERMINATION',
    allowedDDSLines: ['Motor'],
    allowedRedCoop: true,
    allowedStatus: ['ACTIVE'],
    allowedWKZ: ['*'],
    canceledSinceInDays: 0
  })

  // ──────────────────────────────────────────────────────────────────────────────────
  // MOTOR – Begleitendes Fahren (nur AUTO, nur Status ACTIVE, kein RedCoop, Feature-Flag)
  // ──────────────────────────────────────────────────────────────────────────────────
  if (isTrue(featureFlags?.ENABLE_ACCOMPANYING_DRIVING)) {
    rules.push({
      linkname: 'accompanyingDrivingCertificate',
      hrefConfigKey: 'ACCOMPANYING_DRIVING',
      allowedDDSLines: ['Motor'],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['112'],
      canceledSinceInDays: 0
    })
  }

  // ──────────────────────────────────────────────────────────────────────────────────
  // SHUR – Schaden melden (pro Sparte, kein WKZ-Filter da SHUR kein WKZ hat)
  // Reihenfolge: Spezifische Sparten-Regeln vor der generischen Bundle-Regel,
  // da bei gleichen `linkname` nur die erste passende Regel gilt (first-match-wins).
  // ──────────────────────────────────────────────────────────────────────────────────
  rules.push(
    {
      linkname: 'reportDamage',
      hrefConfigKey: 'REPORT_DAMAGE_ACCIDENT',
      allowedDDSLines: ['Accident'],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['*'],
      canceledSinceInDays: 0
    },
    {
      linkname: 'reportDamage',
      hrefConfigKey: 'REPORT_DAMAGE_HOUSEHOLDCONTENTS',
      allowedDDSLines: ['Household'],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['*'],
      canceledSinceInDays: 0
    },
    {
      linkname: 'reportDamage',
      hrefConfigKey: 'REPORT_DAMAGE_LIABILITY',
      allowedDDSLines: ['Liability'],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['*'],
      canceledSinceInDays: 0
    },
    {
      linkname: 'reportDamage',
      hrefConfigKey: 'REPORT_DAMAGE_LEGALPROTECTION',
      allowedDDSLines: ['LegalProtection'],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['*'],
      canceledSinceInDays: 0
    },
    // Generische Bundle-Regel greift für Sparten-Kombinationen oder nicht explizit
    // gelistete Einzelsparten (z. B. Glass, Building). Muss als letzte reportDamage-Regel
    // stehen, da first-match-wins gilt.
    {
      linkname: 'reportDamage',
      hrefConfigKey: 'REPORT_DAMAGE_BUNDLE',
      allowedDDSLines: [
        'Glass',
        'Building',
        'Accident',
        'Household',
        'Liability',
        'LegalProtection'
      ],
      allowedRedCoop: true,
      allowedStatus: ['ACTIVE'],
      allowedWKZ: ['*'],
      canceledSinceInDays: 0
    }
  )

  return rules
}
